import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import styled, { css } from "styled-components";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";
import { FaArrowLeft } from "react-icons/fa6";

import axiosInstance from "../../axiosInstance";
import URL from "../../urls";
import Avatar from "../common/Avatar";
import { Button } from "../common/Buttons";
import CheckBox from "../common/CheckBox";
import {
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  MOSS_GREEN_100,
  MOSS_GREEN_800,
} from "../common/colors";
import FlexBox from "../common/FlexBox";
import { Body2, H3, Support } from "../common/Headings";
import Loader from "../common/Loader";
import { Modal } from "../common/Modal";
import useMobileView from "../../hooks/useMobileView";
import { addToast } from "../../Store/Actions";

const Content = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Header = styled(FlexBox)`
  padding: 1rem 1.5rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${DAVYS_GREY_400};
`;

const TitleBlock = styled(FlexBox)`
  align-items: center;
  gap: 0.5rem;
  @media screen and (max-width: 768px) {
    align-items: flex-start;
  }
`;

const ModalBody = styled(FlexBox)`
  height: 100%;
`;

const List = styled(FlexBox)`
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 8rem;
`;

const ListItem = styled(FlexBox)`
  padding: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;
    `};
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${MOSS_GREEN_100};
    `};
`;

const ModalFooter = styled(FlexBox)`
  padding: 1rem 1.5rem;
  width: 100%;
  position: absolute;
  bottom: 0;
  z-index: 1;
  background-color: white;
  border-top: 1px solid ${DAVYS_GREY_400};
`;

const Title = styled(FlexBox)`
  gap: 0.5rem;
  align-items: center;
  @media screen and (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
    margin-top: -0.35rem;
  }
`;

const FooterContent = styled(FlexBox)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 768px) {
    align-items: flex-start;
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const MarkAttendance = styled(FooterContent)`
  @media screen and (max-width: 768px) {
    align-items: center;
    ${Button} {
      width: 100%;
    }
  }
`;

const SavedInfoSection = styled(FlexBox)`
  flex-direction: column;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const AttendanceModal = () => {
  const [attendanceDetails, setAttendanceDetails] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const isMobile = useMobileView();

  const [queryParams, setQueryParams] = useQueryParams({
    showAttendanceModal: BooleanParam,
    bookingId: StringParam,
  });

  useEffect(() => {
    if (queryParams?.bookingId) {
      try {
        fetchAttendanceDetails();
      } catch (error) {
        Bugsnag.notify(error);
      } finally {
        setLoading(false);
      }
    }
    setLoading(false);
  }, [queryParams?.bookingId]);

  const handleClose = () => {
    setQueryParams({ showAttendanceModal: null, bookingId: null });
  };

  const fetchAttendanceDetails = async () => {
    const url = URL.getAttendanceDetails.replace(
      "<booking_id>",
      queryParams?.bookingId
    );
    const response = await axiosInstance.get(url);
    if (response?.data?.data) {
      setAttendanceDetails(response?.data?.data?.booking_details);
      setCustomers(response?.data?.data?.customers);
    }
  };

  const handleSaveAttendance = async () => {
    try {
      setLoading(true);
      const url = URL.markAttendanceDetails.replace(
        "<booking_id>",
        queryParams?.bookingId
      );
      const payload = {
        customer_ids: customers
          .filter(customer => customer?.attended)
          .map(customer => customer?.id),
      };
      const response = await axiosInstance.post(url, payload);
      if (response?.status === 200) {
        dispatch(
          addToast({
            msg: "Attendance saved successfully",
            autoDismiss: true,
            appearance: "success",
            showBottomToastOnMobile: true,
          })
        );
        // fetch the attendance details again to get the updated data
        fetchAttendanceDetails();
      }
    } catch (error) {
      Bugsnag.notify(error);
    } finally {
      setLoading(false);
    }
  };

  const present = customers?.filter(customer => customer?.attended)?.length;
  const absent = customers?.filter(customer => !customer?.attended)?.length;

  return (
    <Modal M2 togglePopup={handleClose}>
      <Content>
        <Header>
          <TitleBlock>
            {isMobile && (
              <FaArrowLeft color={DAVYS_GREY_800} onClick={handleClose} />
            )}
            <Title>
              <H3 bold>Attendance</H3>
              <Body2 textTransform="capitalize">
                {attendanceDetails?.group_name ? "#" : "Booking ID: "}
                {attendanceDetails?.entity_id} |{" "}
                {dayjs(attendanceDetails?.slotdatetime).format(
                  "DD MMM YYYY, hh:mm A"
                )}
              </Body2>
            </Title>
          </TitleBlock>
          {!isMobile && (
            <FiX
              onClick={handleClose}
              cursor="pointer"
              size="1.25rem"
              strokeWidth={3}
              color={DAVYS_GREY_800}
            />
          )}
        </Header>
        <ModalBody>
          {loading ? (
            <Loader />
          ) : (
            <List className="custom-scrollbar">
              {customers.map((customer, index) => (
                <ListItem
                  key={index}
                  isSelected={customer?.attended}
                  isDisabled={attendanceDetails?.saved_at}
                  onClick={() => {
                    if (attendanceDetails?.saved_at) return;
                    const newCustomers = [...customers];
                    newCustomers[index].attended =
                      !newCustomers[index].attended;
                    setCustomers(newCustomers);
                  }}
                >
                  <FlexBox key={customer?.id} align="center" columnGap="1rem">
                    <Avatar name={customer?.name} />
                    <FlexBox column rowGap="0.25rem">
                      <Body2 color={MOSS_GREEN_800} bold>
                        {customer?.name}
                      </Body2>
                      <Support color={DAVYS_GREY_700}>#{customer?.id}</Support>
                    </FlexBox>
                  </FlexBox>
                  <CheckBox
                    check={customer?.attended}
                    disabled={attendanceDetails?.saved_at}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </ModalBody>
        <ModalFooter>
          {attendanceDetails?.saved_at ? (
            <FooterContent>
              <Body2 bold>
                Present: {present} | Absent: {absent}
              </Body2>
              <SavedInfoSection>
                <Support>
                  Saved on{" "}
                  {dayjs(attendanceDetails?.saved_at).format(
                    "DD MMM YYYY, hh:mm A"
                  )}
                </Support>
                <Support>by {attendanceDetails?.saved_by}</Support>
              </SavedInfoSection>
            </FooterContent>
          ) : (
            <MarkAttendance>
              <Support>
                Once saved, you’ll not be able to edit the attendance.
              </Support>
              <Button primary onClick={handleSaveAttendance} disabled={loading}>
                SAVE ATTENDANCE
              </Button>
            </MarkAttendance>
          )}
        </ModalFooter>
      </Content>
    </Modal>
  );
};

export default AttendanceModal;
